import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const steps = [
  {
    number: '01',
    title: 'Export Your Build Files',
    description:
      'From your Readdy project dashboard, download the production build. This will be a ZIP file containing all compiled HTML, CSS, JS, and assets.',
    icon: 'ri-download-cloud-line',
    details: [
      'Log into your Readdy dashboard',
      'Navigate to your project settings',
      'Look for "Export" or "Download Build" option',
      'Save the ZIP file to your computer',
    ],
  },
  {
    number: '02',
    title: 'Log Into cPanel',
    description:
      'Access your hosting provider\'s cPanel. This is typically at yourdomain.com:2083 or through your hosting provider\'s client area.',
    icon: 'ri-server-line',
    details: [
      'Open your browser and go to your cPanel URL',
      'Enter your cPanel username and password',
      'You should see the cPanel dashboard with various icons',
    ],
  },
  {
    number: '03',
    title: 'Upload Files via File Manager',
    description:
      'Use cPanel\'s File Manager to upload your build files to the public_html directory (or the subdomain directory if using one).',
    icon: 'ri-folder-upload-line',
    details: [
      'In cPanel, click "File Manager"',
      'Navigate to public_html (or your target directory)',
      'Click "Upload" and select your build ZIP file',
      'After upload, right-click the ZIP and select "Extract"',
      'Ensure index.html is in the root of public_html',
    ],
  },
  {
    number: '04',
    title: 'Add the .htaccess File',
    description:
      'Create an .htaccess file in your public_html root to handle React Router\'s client-side routing on Apache servers.',
    icon: 'ri-file-settings-line',
    details: [
      'In File Manager, click "+ File" to create a new file',
      'Name it exactly: .htaccess (with the dot)',
      'Copy the rewrite rules from the section below into this file',
      'Save the file in your public_html root directory',
    ],
  },
  {
    number: '05',
    title: 'Configure Your Domain',
    description:
      'Point your domain to the correct directory. If your domain is already configured, the site should be live immediately.',
    icon: 'ri-global-line',
    details: [
      'In cPanel, go to "Domains" or "Addon Domains"',
      'Ensure your domain points to public_html (or the correct subdirectory)',
      'If using a subdomain, verify the document root is correct',
      'DNS propagation may take up to 24 hours if changing nameservers',
    ],
  },
  {
    number: '06',
    title: 'Test & Verify',
    description:
      'Visit your domain and test all pages, navigation, and forms to ensure everything works correctly on the new host.',
    icon: 'ri-shield-check-line',
    details: [
      'Open your domain in a browser',
      'Test navigation between all pages',
      'Verify images and assets load correctly',
      'Test the contact form submission',
      'Check mobile responsiveness',
    ],
  },
];

const htaccessContent = `<IfModule mod_rewrite.c>
  RewriteEngine On
  RewriteBase /

  # Don't rewrite files or directories
  RewriteCond %{REQUEST_FILENAME} -f [OR]
  RewriteCond %{REQUEST_FILENAME} -d
  RewriteRule ^ - [L]

  # Rewrite everything else to index.html for React Router
  RewriteRule ^ index.html [L]
</IfModule>

# Enable gzip compression for better performance
<IfModule mod_deflate.c>
  AddOutputFilterByType DEFLATE text/html text/css text/javascript application/javascript application/json image/svg+xml
</IfModule>

# Cache static assets
<IfModule mod_expires.c>
  ExpiresActive On
  ExpiresByType image/jpeg "access plus 1 year"
  ExpiresByType image/png "access plus 1 year"
  ExpiresByType image/svg+xml "access plus 1 year"
  ExpiresByType text/css "access plus 1 month"
  ExpiresByType text/javascript "access plus 1 month"
  ExpiresByType application/javascript "access plus 1 month"
</IfModule>`;

const troubleshooting = [
  {
    problem: '404 errors on page refresh or direct links',
    solution:
      'This means the .htaccess file is missing or mod_rewrite is not enabled. Verify the .htaccess file exists in public_html and contains the rewrite rules. Contact your host to enable mod_rewrite if needed.',
  },
  {
    problem: 'Blank white page after upload',
    solution:
      'Check that all build files were extracted properly. Ensure index.html exists in the root. Also verify that JavaScript is enabled in your browser.',
  },
  {
    problem: 'Images or assets not loading',
    solution:
      'Asset paths in the build use relative paths. Make sure files are uploaded to the correct directory structure. Check browser console (F12) for 404 errors on specific files.',
  },
  {
    problem: 'Contact form not submitting',
    solution:
      'The form uses the Readdy Forms API endpoint. This will continue to work regardless of where the site is hosted, as long as the site has internet access. Verify the form URL in the code matches your Readdy form endpoint.',
  },
  {
    problem: 'Site shows "Index of /" instead of the website',
    solution:
      'Your server is showing a directory listing instead of index.html. Make sure index.html is in the root directory. You may also need to set DirectoryIndex index.html in your .htaccess or Apache config.',
  },
];

export default function DeploymentGuide() {
  const [copied, setCopied] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const copyHtaccess = () => {
    navigator.clipboard.writeText(htaccessContent);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-page">
      <Navbar />

      {/* Hero */}
      <section className="pt-28 pb-12 bg-white border-b border-black/[0.06]">
        <div className="mx-auto px-6 lg:px-10 xl:px-16 text-center">
          <span className="eyebrow">Self-Hosting Guide</span>
          <h1 className="text-3xl lg:text-4xl font-extrabold text-page-ink mb-4 tracking-[-0.03em]">
            Deploy to cPanel
          </h1>
          <p className="text-page-muted text-base max-w-2xl mx-auto leading-relaxed font-light">
            A step-by-step guide to migrate your SunStone System website from Readdy hosting to your own cPanel server. No technical expertise required.
          </p>
        </div>
      </section>

      {/* Prerequisites */}
      <section className="py-10 mx-auto px-6 lg:px-10 xl:px-16">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white border border-black/[0.08] rounded-2xl p-8 lg:p-10">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 flex items-center justify-center bg-[#c97a2b]/10 border border-[#c97a2b]/20 rounded-lg">
                <i className="ri-list-check text-[#c97a2b] text-lg" />
              </div>
              <h2 className="text-xl font-bold text-page-ink">What You Will Need</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { icon: 'ri-server-line', text: 'A cPanel hosting account (shared, VPS, or dedicated)' },
                { icon: 'ri-earth-line', text: 'A registered domain name pointed to your hosting' },
                { icon: 'ri-download-cloud-line', text: 'Your Readdy build files (ZIP export from dashboard)' },
                { icon: 'ri-lock-password-line', text: 'cPanel login credentials (username & password)' },
                { icon: 'ri-time-line', text: 'About 15–20 minutes of your time' },
                { icon: 'ri-wifi-line', text: 'Stable internet connection for file upload' },
              ].map((item) => (
                <div key={item.text} className="flex items-start gap-3 p-4 bg-page rounded-xl border border-black/[0.06]">
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                    <i className={`${item.icon} text-[#c97a2b] text-sm`} />
                  </div>
                  <span className="text-page-muted text-sm leading-relaxed">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="py-10 mx-auto px-6 lg:px-10 xl:px-16">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-extrabold text-page-ink mb-8 tracking-[-0.02em] text-center">
            Step-by-Step Migration
          </h2>
          <div className="space-y-6">
            {steps.map((step, index) => (
              <div
                key={step.number}
                className="bg-white border border-black/[0.08] rounded-2xl p-8 lg:p-10 hover:border-[#c97a2b]/20 transition-colors"
              >
                <div className="flex items-start gap-5">
                  <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-[#c97a2b]/10 border border-[#c97a2b]/20 rounded-xl">
                    <i className={`${step.icon} text-[#c97a2b] text-xl`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-[#c97a2b] text-xs font-bold tracking-wider">
                        STEP {step.number}
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-page-ink mb-2">{step.title}</h3>
                    <p className="text-page-muted text-sm leading-relaxed mb-4">{step.description}</p>
                    <ul className="space-y-2">
                      {step.details.map((detail, i) => (
                        <li key={i} className="flex items-start gap-2.5">
                          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <i className="ri-check-line text-[#c97a2b] text-xs" />
                          </div>
                          <span className="text-page-muted text-sm leading-relaxed">{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* .htaccess Section */}
      <section className="py-10 mx-auto px-6 lg:px-10 xl:px-16">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white border border-[#c97a2b]/20 rounded-2xl p-8 lg:p-10">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 flex items-center justify-center bg-[#c97a2b]/10 border border-[#c97a2b]/20 rounded-lg">
                <i className="ri-file-code-line text-[#c97a2b] text-lg" />
              </div>
              <h2 className="text-xl font-bold text-page-ink">The .htaccess File</h2>
            </div>
            <p className="text-page-muted text-sm leading-relaxed mb-6 ml-[52px]">
              This file is critical for React Router to work on Apache servers. It tells the server to serve <code className="text-[#c97a2b] bg-[#c97a2b]/10 px-1.5 py-0.5 rounded text-xs">index.html</code> for all routes, enabling direct links and page refreshes to work correctly.
            </p>

            <div className="relative bg-[#0f0f0f] border border-white/[0.1] rounded-xl p-5 overflow-x-auto">
              <button
                onClick={copyHtaccess}
                className="absolute top-4 right-4 flex items-center gap-2 px-3 py-1.5 bg-[#c97a2b] text-white text-xs font-semibold rounded-md hover:bg-[#b06a22] transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className={`${copied ? 'ri-check-line' : 'ri-file-copy-line'} text-sm`} />
                {copied ? 'Copied!' : 'Copy'}
              </button>
              <pre className="text-xs text-white/70 font-mono leading-relaxed whitespace-pre-wrap pr-24">
                {htaccessContent}
              </pre>
            </div>

            <div className="mt-5 flex items-start gap-3 p-4 bg-[#c97a2b]/5 border border-[#c97a2b]/15 rounded-xl">
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-information-line text-[#c97a2b] text-sm" />
              </div>
              <p className="text-page-muted text-sm leading-relaxed">
                <strong className="text-page-ink">Important:</strong> The file must be named exactly <code className="text-[#c97a2b] bg-[#c97a2b]/10 px-1 py-0.5 rounded text-xs">.htaccess</code> (starting with a dot) and placed in the root of your <code className="text-[#c97a2b] bg-[#c97a2b]/10 px-1 py-0.5 rounded text-xs">public_html</code> directory. If you cannot see dot-files in cPanel File Manager, enable "Show Hidden Files" in the File Manager settings.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* File Structure */}
      <section className="py-10 mx-auto px-6 lg:px-10 xl:px-16">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white border border-black/[0.08] rounded-2xl p-8 lg:p-10">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 flex items-center justify-center bg-[#c97a2b]/10 border border-[#c97a2b]/20 rounded-lg">
                <i className="ri-folders-line text-[#c97a2b] text-lg" />
              </div>
              <h2 className="text-xl font-bold text-page-ink">Expected File Structure</h2>
            </div>
            <p className="text-page-muted text-sm leading-relaxed mb-6">
              After uploading your build and adding the .htaccess file, your public_html directory should look like this:
            </p>
            <div className="bg-[#0f0f0f] border border-white/[0.1] rounded-xl p-5 font-mono text-xs text-white/60 leading-relaxed">
              <div className="text-[#c97a2b] mb-2">public_html/</div>
              <div className="pl-4">├── <span className="text-white/80">.htaccess</span> <span className="text-white/30">← critical for routing</span></div>
              <div className="pl-4">├── <span className="text-white/80">index.html</span> <span className="text-white/30">← main entry point</span></div>
              <div className="pl-4">├── <span className="text-white/80">assets/</span> <span className="text-white/30">← JS, CSS bundles</span></div>
              <div className="pl-8">├── index-xxx.js</div>
              <div className="pl-8">├── index-xxx.css</div>
              <div className="pl-4">└── <span className="text-white/80">images/</span> <span className="text-white/30">← static images</span></div>
            </div>
          </div>
        </div>
      </section>

      {/* Troubleshooting */}
      <section className="py-10 mx-auto px-6 lg:px-10 xl:px-16">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-extrabold text-page-ink mb-8 tracking-[-0.02em] text-center">
            Troubleshooting
          </h2>
          <div className="space-y-3">
            {troubleshooting.map((item, index) => (
              <div
                key={index}
                className="bg-white border border-black/[0.08] rounded-xl overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-5 text-left cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-red-500/10 border border-red-500/20 rounded-lg flex-shrink-0">
                      <i className="ri-error-warning-line text-red-400 text-sm" />
                    </div>
                    <span className="text-page-ink font-semibold text-sm">{item.problem}</span>
                  </div>
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    <i
                      className={`ri-arrow-down-s-line text-page-faint transition-transform duration-200 ${openFaq === index ? 'rotate-180' : ''}`}
                    />
                  </div>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${openFaq === index ? 'max-h-96' : 'max-h-0'}`}
                >
                  <div className="px-5 pb-5 pl-16">
                    <div className="flex items-start gap-2.5">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-lightbulb-line text-[#c97a2b] text-xs" />
                      </div>
                      <p className="text-page-muted text-sm leading-relaxed">{item.solution}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Reference Card */}
      <section className="py-10 mx-auto px-6 lg:px-10 xl:px-16 pb-16">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-[#c97a2b]/10 to-transparent border border-[#c97a2b]/20 rounded-2xl p-8 lg:p-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center bg-[#c97a2b]/10 border border-[#c97a2b]/20 rounded-lg">
                <i className="ri-bookmark-line text-[#c97a2b] text-lg" />
              </div>
              <h2 className="text-xl font-bold text-page-ink">Quick Reference</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { label: 'Server Type', value: 'Apache (cPanel)' },
                { label: 'Required Module', value: 'mod_rewrite' },
                { label: 'Entry File', value: 'index.html' },
                { label: 'Routing Method', value: 'Client-side (React Router)' },
                { label: 'Form Handling', value: 'Readdy API (external)' },
                { label: 'SSL Required', value: 'Recommended (Let\'s Encrypt)' },
              ].map((ref) => (
                <div
                  key={ref.label}
                  className="flex items-center justify-between p-4 bg-page rounded-xl border border-black/[0.06]"
                >
                  <span className="text-page-faint text-xs font-medium">{ref.label}</span>
                  <span className="text-page-ink text-sm font-semibold">{ref.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}